import {
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faChevronDown,
  faPowerOff,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener, Tooltip as MUITooltip } from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { sidebarItems } from 'src/constants/sidebarConstants';
import { addModal, removeModal } from 'src/features/globalSlice';
import { routes } from 'src/routes';
import { logout, switchManufacturer } from 'src/store/actions/authActions';
import { Role } from 'src/store/models/Auth';
import { ModalType } from 'src/store/models/Modal';
import { sessionStorageHelper, StoragePrefix } from 'src/utils/localStorage';
import useMountTransition from 'src/utils/useMountTransition';

import Avatar from '../Avatar/Avatar';
import Button from '../Button/Button';
import SidebarLink from '../SidebarLink/SidebarLink';
import Tooltip from '../Tooltip/Tooltip';
import classes from './Sidebar.module.scss';

export interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  className?: string;
  isTablet?: boolean;
}

const Sidebar: FC<SidebarProps> = ({
  className,
  collapsed,
  setCollapsed,
  isTablet,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>();

  const isCollapsed = useMountTransition(collapsed, 300);
  const hideInfo = collapsed || isCollapsed;

  const user = useAppSelector((state) => state.auth.user);
  const isService = user?.role === Role.SERVICE;

  const [showManufacturers, setShowManufacturers] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !sidebarRef?.current?.contains(e.target as Node) &&
        isTablet &&
        !collapsed
      ) {
        setCollapsed(true);
      }
    };

    const element = document.getElementById('root');
    element.addEventListener('click', handleClickOutside);
    return () => element.removeEventListener('click', handleClickOutside);
  }, [isTablet, setCollapsed, collapsed]);

  const handleClose = () => {
    if (!isTablet) {
      sessionStorageHelper.set<boolean>(
        StoragePrefix.SIDEBAR_COLLAPSED,
        !collapsed
      );
    }
    setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    dispatch(
      addModal({
        title: t('login:logout_are_you_sure'),
        modalType: ModalType.WARNING,
        primaryButtonText: t('common:confirm'),
        secondaryButtonText: t('common:cancel'),
        primaryAction: () => {
          dispatch(removeModal());
          dispatch(logout());
        },
        secondaryAction: () => {
          dispatch(removeModal());
        },
      })
    );
  };

  const userTitle =
    user?.role === Role.MANUFACTURER
      ? user?.manufacturer?.baseCompany?.title
      : user?.title;

  const manufacturers = (
    <div className={classes.Manufacturers}>
      {user?.allUsers?.map((other, i) => (
        <div
          key={i}
          className={classNames({
            [classes.Active]:
              user?.manufacturer?.id === other?.manufacturer?.id,
          })}
          onClick={() => {
            setShowManufacturers(false);
            if (user?.manufacturer?.id !== other?.manufacturer?.id) {
              dispatch(switchManufacturer({ manId: other?.manufacturer?.id }));
            }
          }}
        >
          <Avatar
            title={other?.company?.title}
            subtitle={t(`common:manufacturer`)}
            image={other?.manufacturer?.image}
            imageSize={32}
          />
        </div>
      ))}
    </div>
  );

  return (
    <aside
      className={classNames(classes.Container, className, {
        [classes.Collapsed]: collapsed,
      })}
      ref={sidebarRef}
    >
      <section>
        {!isService ? (
          <Avatar
            title={userTitle}
            subtitle={t(`common:${user?.role}`)}
            showTitles={!hideInfo}
            image={user?.image}
            onClick={() => navigate(routes.SETTINGS)}
          />
        ) : (
          <ClickAwayListener onClickAway={() => setShowManufacturers(false)}>
            <div>
              <MUITooltip
                classes={{ tooltip: classes.Tooltip, popper: classes.Popper }}
                placement="right-start"
                onClose={() => setShowManufacturers(false)}
                open={showManufacturers}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={manufacturers}
              >
                <div>
                  <Avatar
                    title={userTitle}
                    subtitle={
                      isService
                        ? user?.company?.title
                        : t(`common:${user?.role}`)
                    }
                    image={user?.image}
                    showTitles={!hideInfo}
                    imageSize={40}
                    endIcon={
                      !hideInfo && <FontAwesomeIcon icon={faChevronDown} />
                    }
                    onClick={
                      isService ? () => setShowManufacturers(true) : undefined
                    }
                  />
                </div>
              </MUITooltip>
            </div>
          </ClickAwayListener>
        )}
      </section>
      <section>
        {sidebarItems?.[user?.role]?.map((item, i) => (
          <SidebarLink link={item} key={i} index={i} collapsed={collapsed} />
        ))}
      </section>
      <Button
        onClick={handleLogout}
        startIcon={
          <Tooltip
            tooltipText={t('common:logout')}
            placement="right"
            className={classes.LinkTooltip}
            disabled={!collapsed}
          >
            <FontAwesomeIcon icon={faPowerOff} />
          </Tooltip>
        }
        fullWidth
      >
        {!collapsed ? t('common:logout') : null}
      </Button>

      <Button
        onClick={handleClose}
        startIcon={
          <Tooltip
            tooltipText={t('common:expand')}
            placement="right"
            className={classes.LinkTooltip}
            disabled={!collapsed}
          >
            <FontAwesomeIcon
              icon={collapsed ? faChevronDoubleRight : faChevronDoubleLeft}
            />
          </Tooltip>
        }
        className={classes.Collapse}
      ></Button>
    </aside>
  );
};

export default Sidebar;
